import React from 'react';
import { MdKeyboardArrowDown } from 'react-icons/md';
import { useTranslation } from "react-i18next";
import { SearchableDropdownProps } from '../types';
import { getThemeClasses, getDisplayValue, filterItems } from '../utils/locationUtils';
import DropdownItem from './DropdownItem';

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  items,
  selectedValue,
  placeholder,
  searchTerm,
  isOpen,
  loading,
  disabled,
  required,
  theme,
  onToggle,
  onSearch,
  onSelect,
  dropdownRef,
  label,
  error,
  getItemLabel,
}) => {
  const { t } = useTranslation();
  const themeClasses = getThemeClasses(theme);
  const getLabel = getItemLabel || ((item) => item.name);
  const selectedItem = items.find((item) => item.name === selectedValue);
  const displayValue = selectedItem
    ? getLabel(selectedItem)
    : getDisplayValue(selectedValue, placeholder);

  return (
    <div className="w-full">
      {/* Label - conditionally rendered */}
      {label && (
        <label className={`block text-sm font-medium mb-1 ${
          theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
        }`}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Dropdown trigger */}
      <div 
        ref={dropdownRef}
        className="relative"
      >
        <button
          type="button"
          onClick={onToggle}
          disabled={disabled || loading}
          className={`w-full px-4 py-3 text-left border flex items-center justify-between transition-colors cursor-pointer h-[50px] ${
            theme === 'dark' ? 'rounded-md' : 'rounded-lg'
          } ${
            error 
              ? 'border-red-500' 
              : theme === 'dark' 
                ? 'border-gray-600'
                : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent'
          } ${
            disabled 
              ? theme === 'dark' 
                ? 'bg-gray-800 bg-opacity-50 cursor-not-allowed text-gray-500' 
                : 'bg-gray-50 cursor-not-allowed'
              : themeClasses.container
          }`}
        >
          <span className={`truncate max-w-[75%] ${
            !selectedValue 
              ? theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              : theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            {loading ? t("location.loading") : displayValue}
          </span>
          <MdKeyboardArrowDown 
            className={`w-5 h-5 ml-2 transition-transform flex-shrink-0 ${
              isOpen ? 'transform rotate-180' : ''
            } ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
            }`} 
          />
        </button>

        {/* Simple dropdown (non-portal) */}
        {isOpen && (
          <div className="absolute z-20 w-full mt-1 bg-white rounded-md shadow-lg border border-gray-200 max-h-[300px] flex flex-col">
            {/* Search input */}
            <div className="sticky top-0 bg-white p-2 border-b z-10">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => onSearch(e.target.value)}
                placeholder={t("selection.search")}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-700 h-[40px]"
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            {/* Items list */}
            <div className="py-2 overflow-y-auto flex-1">
              {loading ? (
                <div className="px-4 py-4 text-center text-sm text-gray-500">
                  <div className="animate-spin w-4 h-4 border-2 border-orange-600 border-t-transparent rounded-full mx-auto mb-2"></div>
                  {t("location.loading")}
                </div>
              ) : (() => {
                const filteredItems = filterItems(items, searchTerm, getLabel);
                return filteredItems.length === 0 ? (
                  <div className="px-4 py-4 text-center text-sm text-gray-500">
                    {searchTerm
                      ? t("selection.noResults")
                      : t("selection.noOptions")}
                  </div>
                ) : (
                  filteredItems.map((item) => (
                    <DropdownItem
                      key={item.id}
                      item={item}
                      isSelected={item.name === selectedValue}
                      onClick={() => onSelect(item)}
                      theme={theme}
                      displayName={getLabel(item)}
                    />
                  ))
                );
              })()}
            </div>
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <p className="text-red-500 text-xs mt-1">{error}</p>
      )}
    </div>
  );
};

export default SearchableDropdown;
